import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, FileText, Folder, MoreVertical, RefreshCw } from 'react-feather';
import { EditIcon, PublishWhite2, Share, Trash } from '../../../../assets/images';
import { deleteText, editText, shareText, uploadText, uploadTooltipText } from '../data/constants';
import { FolderCardProps } from '../kb.props';
import { truncateData } from '../../../../services';
import StyledTooltip from '../../../../components/tooltip';
import { Loader } from '../../../../components';

function StatusBadge({ status = '', rotation = false }: { status?: string; rotation?: boolean }) {
  const isInProgress = status === 'inprogress' || rotation;
  const isError = status === 'error';
  const isSuccess = status === 'success';

  if (isError) {
    return (
      <span className="rounded-full inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-medium border-1 bg-red-500/15 text-red-600 border-red-500/30">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        Error
      </span>
    );
  }

  if (isInProgress) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border-1 bg-[hsl(var(--button-orange))]/15 text-[hsl(var(--button-orange))] border-[hsl(var(--button-orange))]/30">
        <RefreshCw
          className={`w-3 h-3 ${status === 'inprogress' || rotation ? 'animate-spin' : ''}`}
        />
        In Progress
      </span>
    );
  }

  if (isSuccess) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border-1 border-[hsl(var(--status-success))]/15 bg-[hsl(var(--status-success))]/15 text-[hsl(var(--status-success))]">
        <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--status-success))]" />
        Synced
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border-1 border-[hsl(var(--status-success))]/15 bg-[hsl(var(--status-success))]/15 text-[hsl(var(--status-success))]">
      <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--status-success))]" />
      Synced
    </span>
  );
}

StatusBadge.defaultProps = {
  status: '',
  rotation: false,
};

function FolderCard({
  cardName,
  filesCount,
  isEditAllowed,
  isDeleteAllowed,
  isCreateAllowed,
  isPublic,
  sharedBy,
  folderCardIcon,
  handleDoubleClick,
  handleDeleteClick,
  handleShareClick,
  handleUploadClick,
  handleEditClick,
  onSetActive,
  isSyncAllowed,
  lastUpdatedAt,
  handleSyncClick,
  isActive,
  rotation,
  syncStatus,
  showSyncStatus,
  viewMode = 'grid',
  uploadSyncOverlay = false,
}: FolderCardProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const userDetails = JSON.parse(localStorage.getItem('user_details') || 'null');
  const createdBy = sharedBy || userDetails?.name || 'Unknown';

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDropdownOpen((prev: boolean) => !prev);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') onSetActive(e);
  };

  useLayoutEffect(() => {
    if (isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.right - 192,
      });
    } else {
      setDropdownPosition(null);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inButton = buttonRef.current?.contains(target);
      const inPortal = document.getElementById('folder-card-dropdown-portal')?.contains(target);
      if (!inButton && !inPortal) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderDropdownItem = (
    condition: boolean,
    icon: string,
    alt: string,
    text: string,
    onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
    withBorder = true,
    disabled = false,
    tooltipText = uploadTooltipText as string,
  ) => {
    if (!condition) return null;

    const button = (
      <button
        type="button"
        className={`flex items-center w-full pl-3 py-2 gap-2 text-sm text-gray-700 hover:bg-gray-50 ${
          withBorder ? 'border-b border-gray-100' : ''
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            onClick(e);
            setIsDropdownOpen(false);
          }
        }}
        disabled={disabled}
      >
        <img src={icon} alt={alt} height={16} width={16} className="flex-shrink-0" />
        <span>{text}</span>
      </button>
    );

    return tooltipText && disabled ? (
      <StyledTooltip title={tooltipText}>{button}</StyledTooltip>
    ) : (
      button
    );
  };

  const hasActions = isDeleteAllowed || isEditAllowed || isCreateAllowed || isSyncAllowed;

  const dropdownContent = (
    <div
      id="folder-card-dropdown-portal"
      className="w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
      style={{
        position: 'fixed',
        top: dropdownPosition?.top ?? 0,
        left: dropdownPosition?.left ?? 0,
        zIndex: 9999,
      }}
    >
      {renderDropdownItem(isEditAllowed, EditIcon, 'edit', editText, handleEditClick)}
      {renderDropdownItem(isDeleteAllowed, Trash, 'delete', deleteText, handleDeleteClick)}
      {renderDropdownItem(
        isCreateAllowed,
        PublishWhite2,
        'upload',
        uploadText,
        handleUploadClick,
        true,
        syncStatus === 'inprogress',
      )}
      {isPublic &&
        renderDropdownItem(
          isEditAllowed || isCreateAllowed,
          Share,
          'share',
          shareText,
          handleShareClick,
          false,
        )}
    </div>
  );

  const sharedDropdown = hasActions ? (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        className="p-1.5 rounded-lg text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] transition-all"
        onClick={toggleDropdown}
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {isDropdownOpen && dropdownPosition && createPortal(dropdownContent, document.body)}
    </div>
  ) : null;

  if (viewMode === 'list') {
    return (
      <div
        data-folder-card
        className={`group relative flex items-center gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer ${
          isActive
            ? 'border-2 border-[hsl(var(--primary))]/60 bg-[hsl(var(--primary))]/5 shadow-md'
            : 'border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:border-[hsl(var(--primary))]/30 hover:shadow-sm'
        }`}
        onClick={onSetActive}
        onDoubleClick={handleDoubleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKey}
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))]/20 to-[hsl(var(--primary))]/5 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          {folderCardIcon ? (
            <img src={folderCardIcon} alt="Folder" className="w-5 h-5" />
          ) : (
            <Folder className="w-5 h-5 text-[hsl(var(--primary))]" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <StyledTooltip title={cardName}>
              <h4 className="font-semibold text-[hsl(var(--foreground))] truncate text-sm">
                {truncateData(cardName, 24)}
              </h4>
            </StyledTooltip>
            {isActive && (
              <div className="w-4 h-4 rounded-full bg-[hsl(var(--button-teal))] flex items-center justify-center flex-shrink-0">
                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
              </div>
            )}
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
            by {truncateData(createdBy, 16)}
          </p>
        </div>

        <div className="hidden md:grid grid-cols-2 gap-6 items-center text-xs text-[hsl(var(--muted-foreground))] min-w-[180px]">
          <div className="flex items-center justify-center gap-1.5">
            <FileText className="w-3.5 h-3.5" />
            <span>{filesCount} files</span>
          </div>
          <div className="flex justify-center">
            <span>{lastUpdatedAt}</span>
          </div>
        </div>

        <div className="w-max flex justify-center flex-shrink-0">
          <StatusBadge status={syncStatus} rotation={rotation} />
        </div>

        {showSyncStatus && (
          <StyledTooltip title="Sync Now">
            <button
              type="button"
              disabled={syncStatus === 'inprogress' || rotation}
              onClick={(e) => {
                e.stopPropagation();
                handleSyncClick?.(e);
              }}
              className={`h-8 w-8 flex items-center justify-center rounded transition-colors ${
                syncStatus === 'inprogress' || rotation
                  ? 'cursor-not-allowed text-gray-400'
                  : 'text-[hsl(var(--button-teal))] hover:bg-[hsl(var(--button-teal))]/10'
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${rotation ? 'animate-spin' : ''}`} />
            </button>
          </StyledTooltip>
        )}

        {sharedDropdown}
        {uploadSyncOverlay && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-[hsl(var(--background))]/70 backdrop-blur-[1px]">
            <Loader width="10px" height="10px" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-folder-card
      data-tour="kb-tour-content"
      className={`group relative rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer ${
        isActive
          ? 'border-2 border-[hsl(var(--primary))]/60 bg-[hsl(var(--primary))]/5'
          : 'border border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:border-[hsl(var(--primary))]/30 hover:shadow-md'
      }`}
      onClick={onSetActive}
      onDoubleClick={handleDoubleClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKey}
    >
      {/* Gradient header */}
      <div className="h-2 bg-gradient-to-r from-[#0A8BC7] via-[#0A8BC7]/60 to-[#0A8BC7]/40" />

      <div className="p-3">
        {/* Menu */}
        {hasActions && (
          <div className="absolute top-5 right-0 z-10" ref={dropdownRef}>
            <button
              ref={buttonRef}
              type="button"
              className="p-1.5 mt-2 mr-2 rounded-lg text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] transition-all"
              onClick={toggleDropdown}
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {isDropdownOpen && dropdownPosition && createPortal(dropdownContent, document.body)}
          </div>
        )}

        {/* Folder icon and name */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))]/20 to-[hsl(var(--primary))]/5 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform shadow-sm">
            {folderCardIcon ? (
              <img src={folderCardIcon} alt="Folder" className="w-8 h-8" />
            ) : (
              <FileText className="w-6 h-6 text-[hsl(var(--primary))]" />
            )}
          </div>
          <div className="flex-1 min-w-0 pt-1">
            <StyledTooltip title={cardName}>
              <h4 className="font-semibold text-[16px] text-[hsl(var(--foreground))] truncate">
                {truncateData(cardName, 12)}
              </h4>
            </StyledTooltip>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              by {truncateData(createdBy, 12)}
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/50 px-2 py-1 rounded-md">
            <FileText className="w-3 h-3" />
            <span>{filesCount} files</span>
          </div>
        </div>

        {/* Footer with status and sync */}
        <div className="flex items-center justify-between pt-3 border-t border-[hsl(var(--border))]/50">
          <StatusBadge status={syncStatus} rotation={rotation} />
          <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{lastUpdatedAt}</span>

          {showSyncStatus && syncStatus !== 'inprogress' && (
            <button
              type="button"
              disabled={syncStatus === 'inprogress' || rotation}
              onClick={(e) => {
                e.stopPropagation();
                handleSyncClick?.(e);
              }}
              className={`h-7 px-2 rounded text-xs gap-1.5 flex items-center -mr-2 transition-colors ${
                syncStatus === 'inprogress' || rotation
                  ? 'cursor-not-allowed text-gray-400'
                  : 'text-[hsl(var(--button-teal))] hover:bg-[hsl(var(--button-teal))]/10'
              }`}
            >
              {syncStatus === 'inprogress' || rotation ? '' : 'Sync'}
            </button>
          )}
        </div>
      </div>
      {uploadSyncOverlay && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[hsl(var(--background))]/70 backdrop-blur-[1px]">
          <Loader width="10px" height="10px" />
        </div>
      )}
    </div>
  );
}

export default FolderCard;
